import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import Loading from "../../ui/loading";
import { fetchSectionAttribute } from "../../../services/section-attributes";
import { SectionAttributeContext } from "../../../providers/SectionAttributeProvider";

const ReviewsForm = ({ section, isSubmitting }) => {
  const [isLoading, setIsloading] = useState(true);
  const { sectionAttribute, setSectionAttribute } = useContext(
    SectionAttributeContext
  );

  const getSectionAttribute = useCallback(async () => {
    if (section) {
      try {
        const response = await fetchSectionAttribute(section);
        setSectionAttribute(response.data);
        setIsloading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  }, [section, setSectionAttribute]);

  const shouldFetch = useMemo(
    () => Object.keys(sectionAttribute).length === 0,
    [sectionAttribute]
  );

  useEffect(() => {
    if (shouldFetch) {
      getSectionAttribute();
    }
  }, [shouldFetch, getSectionAttribute]);

  const handleUpdate = (e) => {
    const { name, value } = e.target;

    setSectionAttribute((prevState) => {
      // Split name on levels if is nested, i.e: "data.services[0].name"
      const keys = name.split(".");

      // Recursive function to create the new nested value
      const updateNestedState = (obj, keys, value) => {
        const [firstKey, ...restKeys] = keys;
        if (restKeys.length === 0) {
          // Last key, update the value
          return {
            ...obj,
            [firstKey]: value,
          };
        }

        // Handle arrays inside data
        if (firstKey.endsWith("]")) {
          // Get array index, i.e.: "services[0]"
          const arrayKey = firstKey.slice(0, firstKey.indexOf("["));
          const index = parseInt(
            firstKey.slice(firstKey.indexOf("[") + 1, firstKey.indexOf("]")),
            10
          );

          return {
            ...obj,
            [arrayKey]: obj[arrayKey].map((item, idx) =>
              idx === index ? updateNestedState(item, restKeys, value) : item
            ),
          };
        }

        // No final key, keep nesting
        return {
          ...obj,
          [firstKey]: updateNestedState(obj[firstKey], restKeys, value),
        };
      };

      // If key is nested, start from prevState.data
      if (keys[0] === "data") {
        return {
          ...prevState,
          data: updateNestedState(prevState.data, keys.slice(1), value),
        };
      }

      // If key is not nested, update to superior level
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  if (isLoading)
    return (
      <div className="w-full">
        <Loading />
      </div>
    );

  return (
    <form className="grid w-full items-start gap-6">
      <fieldset className="grid gap-6 rounded-lg border p-4">
        <legend className="-ml-1 px-1 text-sm font-medium">Reviews</legend>
        <div className="grid gap-3">
          <Label htmlFor="Nombre">Review I - Nombre</Label>
          <Input
            id="data.reviews.0.title"
            type="text"
            name="data.reviews[0].title"
            placeholder="Escribe el titulo de Review"
            value={sectionAttribute?.data?.reviews?.[0]?.title}
            onChange={handleUpdate}
            disabled={isSubmitting}
          />
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div className="grid gap-3">
            <Label htmlFor="top-p">Review I - Descripción</Label>
            <Textarea
              id="data.reviews.0.description"
              type="text"
              name="data.reviews[0].description"
              placeholder="Escribe el descripción de Review"
              value={sectionAttribute?.data?.reviews?.[0]?.description}
              onChange={handleUpdate}
              disabled={isSubmitting}
              className="min-h-[4.5rem]"
            />
          </div>
        </div>
        <hr />
        <div className="grid gap-3">
          <Label htmlFor="Nombre">Review II - Nombre</Label>
          <Input
            id="data.reviews.1.title"
            type="text"
            name="data.reviews[1].title"
            placeholder="Escribe el titulo de Review"
            value={sectionAttribute?.data?.reviews?.[1]?.title}
            onChange={handleUpdate}
            disabled={isSubmitting}
          />
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div className="grid gap-3">
            <Label htmlFor="top-p">Review II - Descripción</Label>
            <Textarea
              id="data.reviews.1.description"
              type="text"
              name="data.reviews[1].description"
              placeholder="Escribe el descripción de Review"
              value={sectionAttribute?.data?.reviews?.[1]?.description}
              onChange={handleUpdate}
              disabled={isSubmitting}
              className="min-h-[4.5rem]"
            />
          </div>
        </div>
        <hr />
        <div className="grid gap-3">
          <Label htmlFor="Nombre">Review III - Nombre</Label>
          <Input
            id="data.reviews.2.title"
            type="text"
            name="data.reviews[2].title"
            placeholder="Escribe el titulo de Review"
            value={sectionAttribute?.data?.reviews?.[2]?.title}
            onChange={handleUpdate}
            disabled={isSubmitting}
          />
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div className="grid gap-3">
            <Label htmlFor="top-p">Review III - Descripción</Label>
            <Textarea
              id="data.reviews.2.description"
              type="text"
              name="data.reviews[2].description"
              placeholder="Escribe el descripción de Review"
              value={sectionAttribute?.data?.reviews?.[2]?.description}
              onChange={handleUpdate}
              disabled={isSubmitting}
              className="min-h-[4.5rem]"
            />
          </div>
        </div>
        <hr />
        <div className="grid gap-3">
          <Label htmlFor="Nombre">Review IV - Nombre</Label>
          <Input
            id="data.reviews.3.title"
            type="text"
            name="data.reviews[3].title"
            placeholder="Escribe el titulo de Review"
            value={sectionAttribute?.data?.reviews?.[3]?.title}
            onChange={handleUpdate}
            disabled={isSubmitting}
          />
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div className="grid gap-3">
            <Label htmlFor="top-p">Review IV - Descripción</Label>
            <Textarea
              id="data.reviews.3.description"
              type="text"
              name="data.reviews[3].description"
              placeholder="Escribe el descripción de Review"
              value={sectionAttribute?.data?.reviews?.[3]?.description}
              onChange={handleUpdate}
              disabled={isSubmitting}
              className="min-h-[4.5rem]"
            />
          </div>
        </div>
        <hr />
        <div className="grid gap-3">
          <Label htmlFor="Nombre">Review V - Nombre</Label>
          <Input
            id="data.reviews.4.title"
            type="text"
            name="data.reviews[4].title"
            placeholder="Escribe el titulo de Review"
            value={sectionAttribute?.data?.reviews?.[4]?.title}
            onChange={handleUpdate}
            disabled={isSubmitting}
          />
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div className="grid gap-3">
            <Label htmlFor="top-p">Review V - Descripción</Label>
            <Textarea
              id="data.reviews.4.description"
              type="text"
              name="data.reviews[4].description"
              placeholder="Escribe el descripción de Review"
              value={sectionAttribute?.data?.reviews?.[4]?.description}
              onChange={handleUpdate}
              disabled={isSubmitting}
              className="min-h-[4.5rem]"
            />
          </div>
        </div>
      </fieldset>
    </form>
  );
};

export default ReviewsForm;
